import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Chart } from 'chart.js';
import { ChartRendererSupabase } from '../charts/ChartRendererSupabase';
import { PolicyImpactResults, PolicyScenario, type PolicyLoadProgress } from '../../services/policyImpactService';
import { generatePolicyTimeSeriesData, TimeSeriesEmissionData, applyPolicyTransformations } from '../../services/policyTimeSeriesService';
import type { BuildingsBauScenario } from '../../services/buildingsBauPathways';
import type { EfficiencyLayerOptions } from '../../services/buildingsEfficiencyModel';
import { getCachedBaseline } from '../../services/policyBaselineCache';
import { legendLabelsReflectLineDash } from './chartJsLegendLineDash';
import './PolicyImpactChart.css';
import { supabaseClient } from '../../services/supabase';
import { POLICY_TIMESERIES_BUILDING_EMISSION_COLORS } from '../../theme/navChartPalette';

// Register a global plugin for vertical separator line
const verticalSeparatorPlugin = {
  id: 'verticalSeparator',
  afterDraw: (chart: any) => {
    // Only draw on charts that explicitly opt-in (Policy Tool charts)
    if (chart?.options?.plugins?.verticalSeparator?.enabled !== true) return;
    drawVerticalSeparator(chart);
  },
};

// Register the plugin
Chart.register(verticalSeparatorPlugin);

interface PolicyImpactChartProps {
  results: PolicyImpactResults | null;
  loading: boolean;
  /** Step progress while the parent runs calculatePolicyImpact (summary metrics). */
  loadProgress?: PolicyLoadProgress | null;
  scenario: PolicyScenario;
  location: string;
  chartType?: 'line' | 'contributions' | 'forecast' | 'stacked';
  targetReductionPercent?: number;
  targetBaselineYear?: number;
  /** Year by which the reduction target is reached (linear path from `targetBaselineYear`). */
  targetEndYear?: number;
  emfacVersion?: '2021' | '2025';
  perCapitaMode?: boolean;
  buildingsBauScenario?: BuildingsBauScenario;
  /** RNG share 0–100 for building NG (BAU assumption); overrides scenario default when set */
  buildingsRngPercent?: number;
  /** Heat pump COP (2.5–4); default 3.5 */
  buildingsCop?: number;
  /** Optional efficiency layer options (e.g. housing-based existing/new share). When set, passed to time series generation. */
  efficiencyLayerOptions?: EfficiencyLayerOptions;
  onTotalsChange?: (totals: {
    baselineYear: number;
    baselineTotal: number;
    projectedYear: number;
    projectedWithPoliciesTotal: number;
    totalReduction: number;
    percentReduction: number;
  }) => void;
  /** Called when time series data is ready (e.g. for Building Electrification panel charts). */
  onTimeSeriesDataChange?: (baselineData: TimeSeriesEmissionData[], projectedData: TimeSeriesEmissionData[]) => void;
}

// Cache for time series data to avoid redundant calculations
const timeSeriesCache = new Map<string, {
  baselineData: TimeSeriesEmissionData[];
  projectedData: TimeSeriesEmissionData[];
}>();

/** Clear chart time-series cache (e.g. when EMFAC version or buildings BAU changes). */
export function clearPolicyTimeSeriesCache(): void {
  timeSeriesCache.clear();
}

function baselineCacheKeyFor(
  location: string,
  emfacVersion: '2021' | '2025' = '2025',
  buildingsBauScenario: BuildingsBauScenario = 'current_policy',
): string {
  return `baseline:${location}:emfac${emfacVersion}:buildingsBau${buildingsBauScenario}`;
}

// Chart year range: include GHG inventory 2005-2023; first year of policy effect is 2024
export const CHART_BASELINE_YEAR = 2023;
export const FIRST_POLICY_YEAR = 2024;
/** First year to show in GHG Forecast line/stacked charts (GHG inventory start). */
const CHART_INVENTORY_START_YEAR = 2005;
/** Line charts (and stacked area) include full inventory (2005) through 2045. */
const LINE_CHART_START_YEAR = CHART_INVENTORY_START_YEAR;

// Create a stable key from scenario and location
function createScenarioKey(location: string, scenario: PolicyScenario, emfacVersion: '2021' | '2025' = '2025'): string {
  return `${location}:${emfacVersion}:${scenario.evAdoptionPercent}:${scenario.vmtReductionPercent}:${(scenario as any).energyEfficiencyTargetPercent ?? (scenario as any).energyEfficiencyPercent ?? 0}:${scenario.heatPumpAdoptionPercent}:${(scenario as any).gridCleanElectricityYear || 2045}:${scenario.solarCapacityMultiplier}:${scenario.hdvElectrificationPercent}:${(scenario as any).organicsDiversionPercent || 0}:${scenario.vmtReductionComponents?.transitOrientedDevelopment || 0}:${scenario.vmtReductionComponents?.activeTransportation || 0}:${scenario.vmtReductionComponents?.transitImprovements || 0}:${(scenario as any).downtownHomesPercent ?? 70}:${scenario.parkingPricing?.priceIncreasePercent || 0}:${scenario.micromobility?.scalePercent || 0}`;
}

// Helper function to draw vertical separator line at year 2023
function drawVerticalSeparator(chart: any) {
  try {
    // Safety: never draw unless chart opts-in
    if (chart?.options?.plugins?.verticalSeparator?.enabled !== true) return;

    const ctx = chart.ctx;
    const xScale = chart.scales.x;
    const yScale = chart.scales.y;
    
    if (!xScale || !yScale || !ctx) {
      return;
    }
    
    // Find the index of 2023 in the labels array
    const labels = chart.data.labels || [];
    const index2023 = labels.findIndex((label: string | number) => 
      String(label) === '2023' || label === 2023
    );
    
    if (index2023 === -1) {
      return;
    }
    
    // Get pixel position using getPixelForTick (works with category scales)
    let xPosition: number | undefined;
    
    if (xScale.getPixelForTick) {
      // Use getPixelForTick if available (for category scales)
      const tick2023 = xScale.getPixelForTick(index2023);
      if (tick2023 !== undefined && !isNaN(tick2023)) xPosition = tick2023;
    } else {
      // Fallback: use getPixelForValue with label index
      const tick2023 = xScale.getPixelForValue(labels[index2023]);
      if (tick2023 !== undefined && !isNaN(tick2023)) xPosition = tick2023;
    }
    
    // Validate the position is within reasonable bounds (chart width is typically < 2000px)
    if (xPosition !== undefined && !isNaN(xPosition) && xPosition > 0 && xPosition < 10000) {
      // Draw vertical line from top to bottom of chart area
      const topY = yScale.top;
      const bottomY = yScale.bottom;
      
      ctx.save();
      // Solid, dark gray separator at 2023 (subtle but bolder than grid)
      ctx.strokeStyle = 'rgba(55, 65, 81, 0.85)'; // gray-700
      ctx.lineWidth = 1.75;
      ctx.setLineDash([]); // solid
      ctx.lineCap = 'round';
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.globalAlpha = 1.0;
      ctx.beginPath();
      ctx.moveTo(xPosition, topY);
      ctx.lineTo(xPosition, bottomY);
      ctx.stroke();
      ctx.restore();
    }
  } catch (error) {
    console.error('drawVerticalSeparator error:', error);
  }
}

/** Draw Target trajectory after all datasets so it sits on top of stacked areas (yTotals draws before y stack). */
function drawTargetTrajectoryOnTop(chart: any) {
  try {
    if (chart?.options?.plugins?.targetTrajectoryOnTop?.enabled !== true) return;
    const idx = chart.data.datasets.findIndex((d: any) => d?.label === 'Target');
    if (idx === -1) return;
    const meta = chart.getDatasetMeta(idx);
    if (!meta || meta.hidden) return;
    const ctx = chart.ctx;
    if (!ctx) return;
    const ds = chart.data.datasets[idx];
    const xScale = chart.scales.x;
    const yScale = chart.scales.yTotals;
    if (!ds?.data || !Array.isArray(ds.data) || !xScale || !yScale) return;

    const points = meta.data || [];
    ctx.save();
    ctx.setLineDash([10, 5]);
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.9)';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    let started = false;

    const addFromMeta = () => {
      for (const pt of points) {
        if (!pt || pt.skip) continue;
        let x: number;
        let py: number;
        if (typeof pt.getProps === 'function') {
          const p = pt.getProps(['x', 'y'], true);
          x = p.x;
          py = p.y;
        } else {
          x = pt.x;
          py = pt.y;
        }
        if (typeof x !== 'number' || typeof py !== 'number' || !Number.isFinite(x) || !Number.isFinite(py)) continue;
        if (!started) {
          ctx.moveTo(x, py);
          started = true;
        } else ctx.lineTo(x, py);
      }
    };
    addFromMeta();

    // Fallback: Chart may omit element x/y (e.g. borderWidth 0) or parsed.y may not match yTotals — use scales + raw data
    if (!started) {
      const dataArr = ds.data as (number | null)[];
      const labels = chart.data.labels || [];
      for (let i = 0; i < dataArr.length; i++) {
        const v = dataArr[i];
        if (v == null || typeof v !== 'number' || !Number.isFinite(v)) continue;
        let xPix: number | undefined;
        if (typeof xScale.getPixelForTick === 'function') {
          xPix = xScale.getPixelForTick(i);
        }
        if (xPix === undefined || Number.isNaN(xPix)) {
          const lbl = labels[i];
          xPix = xScale.getPixelForValue(lbl, i);
        }
        const yPix = yScale.getPixelForValue(v);
        if (typeof xPix !== 'number' || typeof yPix !== 'number' || !Number.isFinite(xPix) || !Number.isFinite(yPix)) continue;
        if (!started) {
          ctx.moveTo(xPix, yPix);
          started = true;
        } else ctx.lineTo(xPix, yPix);
      }
    }

    if (started) ctx.stroke();
    ctx.restore();
  } catch (e) {
    console.error('drawTargetTrajectoryOnTop error:', e);
  }
}

const targetTrajectoryOnTopPlugin = {
  id: 'targetTrajectoryOnTop',
  afterDraw(chart: any) {
    drawTargetTrajectoryOnTop(chart);
  },
};

// Register after verticalSeparator so afterDraw runs: separator first, then target on top
Chart.register(targetTrajectoryOnTopPlugin);

function getNiceAxisMaxAndStep(maxValue: number, desiredTickCount = 6): { max: number; stepSize: number } {
  const max = Number.isFinite(maxValue) ? Math.max(0, maxValue) : 0;
  if (max === 0) return { max: 0, stepSize: 1 };

  const roughStep = max / desiredTickCount;
  const exponent = Math.floor(Math.log10(roughStep));
  const base = Math.pow(10, exponent);
  const fraction = roughStep / base;

  let niceFraction: number;
  if (fraction <= 1) niceFraction = 1;
  else if (fraction <= 2) niceFraction = 2;
  else if (fraction <= 5) niceFraction = 5;
  else niceFraction = 10;

  const stepSize = Math.max(1, Math.round(niceFraction * base));
  const niceMax = Math.ceil(max / stepSize) * stepSize;
  return { max: niceMax, stepSize };
}

const PolicyImpactChart: React.FC<PolicyImpactChartProps> = ({
  results,
  loading,
  loadProgress = null,
  scenario,
  location,
  chartType = 'line',
  targetReductionPercent = 80,
  targetBaselineYear = 2015,
  targetEndYear = 2045,
  emfacVersion = '2025',
  perCapitaMode = false,
  buildingsBauScenario = 'current_policy',
  buildingsRngPercent,
  buildingsCop,
  efficiencyLayerOptions,
  onTotalsChange,
  onTimeSeriesDataChange,
}) => {
  const [timeSeriesData, setTimeSeriesData] = useState<{
    baselineData: TimeSeriesEmissionData[];
    projectedData: TimeSeriesEmissionData[];
  } | null>(null);
  const [timeSeriesLoading, setTimeSeriesLoading] = useState(false);
  const [timeSeriesProgress, setTimeSeriesProgress] = useState<PolicyLoadProgress | null>(null);

  const tsGenOptions = useMemo(
    () => ({
      ...(efficiencyLayerOptions ? { efficiencyLayerOptions } : {}),
      onTimeSeriesProgress: (p: PolicyLoadProgress) => setTimeSeriesProgress(p),
    }),
    [efficiencyLayerOptions]
  );

  // Forecast datasets (BAU vs With Policies) with wedge areas (VMT, EV)
  const [forecastData, setForecastData] = useState<null | {
    years: number[];
    bauTotals: number[];
    withPoliciesTotals: number[];
    vmtWedge: number[];
    evWedge: number[];
  }>(null);

  const scenarioKey = useMemo(() => createScenarioKey(location, scenario, emfacVersion), [location, scenario, emfacVersion]);
  const timeSeriesCacheKey = useMemo(() => `${scenarioKey}:buildingsBau${buildingsBauScenario || 'current_policy'}:rng${buildingsRngPercent ?? 'def'}:cop${buildingsCop ?? 'def'}:effOpts${efficiencyLayerOptions ? '1' : '0'}`, [scenarioKey, buildingsBauScenario, buildingsRngPercent, buildingsCop, efficiencyLayerOptions]);

  const onTimeSeriesDataChangeRef = useRef(onTimeSeriesDataChange);
  onTimeSeriesDataChangeRef.current = onTimeSeriesDataChange;

  const structuralDepsKey = `${location}:${emfacVersion}:${buildingsBauScenario || 'current_policy'}`;
  const prevStructuralDepsKeyRef = useRef(structuralDepsKey);

  const storeTimeSeriesResult = (
    cacheKey: string,
    data: { baselineData: TimeSeriesEmissionData[]; projectedData: TimeSeriesEmissionData[]; location: string },
    cancelled: () => boolean,
  ) => {
    if (cancelled()) return;
    timeSeriesCache.set(cacheKey, data);
    if (timeSeriesCache.size > 10) {
      const firstKey = timeSeriesCache.keys().next().value;
      if (firstKey) {
        timeSeriesCache.delete(firstKey);
      }
    }
    setTimeSeriesData(data);
    onTimeSeriesDataChangeRef.current?.(data.baselineData, data.projectedData);
  };

  useEffect(() => {
    let cancelled = false;
    const isCancelled = () => cancelled;

    if (prevStructuralDepsKeyRef.current !== structuralDepsKey) {
      prevStructuralDepsKeyRef.current = structuralDepsKey;
      clearPolicyTimeSeriesCache();
      setTimeSeriesData(null);
    }

    const generateTimeSeries = async () => {
      if (!location) return;
      const cacheKey = timeSeriesCacheKey;
      if (timeSeriesCache.has(cacheKey)) {
        const cached = timeSeriesCache.get(cacheKey);
        if (cached) {
          storeTimeSeriesResult(cacheKey, { ...cached, location }, isCancelled);
          return;
        }
      }

      const baselineCacheKey = baselineCacheKeyFor(location, emfacVersion, buildingsBauScenario);
      const baseline = getCachedBaseline(baselineCacheKey);

      if (baseline) {
        try {
          const transformOptions = {
            ...(buildingsRngPercent != null ? { rngOverridePercent: buildingsRngPercent } : {}),
            ...(buildingsCop != null ? { cop: buildingsCop } : {}),
            ...(efficiencyLayerOptions ? { efficiencyLayerOptions } : {}),
          };
          const transformedProjections = applyPolicyTransformations(
            baseline,
            scenario,
            Object.keys(transformOptions).length > 0 ? transformOptions : undefined,
          );

          const historicalUpTo2022 = baseline.historicalData.filter((d: TimeSeriesEmissionData) => d.year < 2023);
          const projectedWithPolicies = [...historicalUpTo2022, ...transformedProjections];
          const baselineData = [...historicalUpTo2022, ...baseline.baselineProjections];

          storeTimeSeriesResult(
            cacheKey,
            { baselineData, projectedData: projectedWithPolicies, location },
            isCancelled,
          );
        } catch (error) {
          console.error('Error applying policy transformations:', error);
          setTimeSeriesProgress(null);
          setTimeSeriesLoading(true);
          try {
            const data = await generatePolicyTimeSeriesData(
              location,
              scenario,
              emfacVersion,
              buildingsBauScenario,
              tsGenOptions,
            );
            storeTimeSeriesResult(cacheKey, data, isCancelled);
          } catch (err) {
            console.error('Error generating time series data:', err);
          } finally {
            if (!cancelled) {
              setTimeSeriesLoading(false);
              setTimeSeriesProgress(null);
            }
          }
        }
      } else {
        setTimeSeriesProgress(null);
        setTimeSeriesLoading(true);
        try {
          const data = await generatePolicyTimeSeriesData(
            location,
            scenario,
            emfacVersion,
            buildingsBauScenario,
            tsGenOptions,
          );
          storeTimeSeriesResult(cacheKey, data, isCancelled);
        } catch (error) {
          console.error('Error generating time series data:', error);
        } finally {
          if (!cancelled) {
            setTimeSeriesLoading(false);
            setTimeSeriesProgress(null);
          }
        }
      }
    };

    void generateTimeSeries();

    return () => {
      cancelled = true;
    };
  }, [location, timeSeriesCacheKey, emfacVersion, buildingsBauScenario, buildingsRngPercent, buildingsCop, scenario, tsGenOptions, structuralDepsKey]);

  // Compute per-year VMT series so we can show LDV intensity (kg/mile) in tooltips.
  // This helps distinguish "emissions changed because VMT changed" vs "intensity changed".
  const vmtByYear = useMemo(() => {
    // Baseline might be computed asynchronously; re-check once timeSeriesData is available.
    // Using `timeSeriesData` as a dependency ensures we recompute after the baseline cache warms.
    const baseline = getCachedBaseline(baselineCacheKeyFor(location, emfacVersion, buildingsBauScenario));
    if (!baseline) return null;

    // Build a simple interpolator for EMFAC forecasting ratios.
    const rows = Array.isArray((baseline as any).emfacData) ? ((baseline as any).emfacData as any[]) : [];
    const byYear = new Map<number, any>();
    rows.forEach((r) => {
      const y = Number(r?.year);
      if (!Number.isFinite(y)) return;
      byYear.set(y, r);
    });
    const years = Array.from(byYear.keys()).sort((a, b) => a - b);
    const getRatio = (y: number): number => {
      const exact = byYear.get(y)?.forecasting_ratio;
      if (typeof exact === 'number' && Number.isFinite(exact) && exact > 0) return exact;
      if (years.length === 0) return 1;
      // Find bracketing years; linear interpolate.
      let lo: number | undefined;
      let hi: number | undefined;
      for (let i = 0; i < years.length; i++) {
        const yy = years[i];
        if (yy < y) lo = yy;
        if (yy > y) { hi = yy; break; }
      }
      const loVal = lo !== undefined ? byYear.get(lo)?.forecasting_ratio : undefined;
      const hiVal = hi !== undefined ? byYear.get(hi)?.forecasting_ratio : undefined;
      if (lo !== undefined && hi !== undefined && lo !== hi &&
          typeof loVal === 'number' && Number.isFinite(loVal) &&
          typeof hiVal === 'number' && Number.isFinite(hiVal)) {
        const t = (y - lo) / (hi - lo);
        return loVal + (hiVal - loVal) * t;
      }
      // Extrapolate with last known value.
      const last = years[years.length - 1];
      const lastVal = byYear.get(last)?.forecasting_ratio;
      return (typeof lastVal === 'number' && Number.isFinite(lastVal) && lastVal > 0) ? lastVal : 1;
    };

    const baselineVmt2023 = Number((baseline as any).baselineVMT) || 0;
    const out = new Map<number, { baselineVmt: number; policyVmt: number }>();
    const totalSteps = 2045 - 2023;

    for (let y = 2005; y <= 2045; y++) {
      const ratio = getRatio(y);
      const baseVmt = baselineVmt2023 > 0 ? baselineVmt2023 * ratio : 0;
      const yearsElapsed = y - 2023;
      const annualVmtr = (Number(scenario.vmtReductionPercent) || 0) / totalSteps;
      const vmtReductionPct = yearsElapsed <= 0 ? 0 : Math.min(Number(scenario.vmtReductionPercent) || 0, annualVmtr * yearsElapsed);
      const policyVmt = baseVmt * (1 - vmtReductionPct / 100);
      out.set(y, { baselineVmt: baseVmt, policyVmt });
    }

    return out;
  }, [scenario.vmtReductionPercent, location, emfacVersion, buildingsBauScenario, timeSeriesData]);

  // Cache for forecast data
  const forecastCache = useRef(new Map<string, {
    years: number[];
    bauTotals: number[];
    withPoliciesTotals: number[];
    vmtWedge: number[];
    evWedge: number[];
  }>());

  // Build forecast data whenever chart type or scenario changes
  useEffect(() => {
    if (chartType !== 'forecast' || !location || !results) {
      setForecastData(null);
      return;
    }

    let cancelled = false;

    const buildForecast = async () => {
      try {
        // Create cache key for forecast
        const forecastCacheKey = `${scenarioKey}:forecast:${results.baseline.currentEVPercent}`;
        
        // Check cache first
        if (forecastCache.current.has(forecastCacheKey)) {
          const cached = forecastCache.current.get(forecastCacheKey);
          if (cached) {
            if (!cancelled) setForecastData(cached);
            return;
          }
        }

        // Helper: zero-out all non-transport levers so BAU and isolated cases are unaffected by unrelated policies
        const zeroedPolicies = {
          heatPumpAdoptionPercent: 0,
          solarCapacityMultiplier: 1,
          hdvElectrificationPercent: 0,
          vmtReductionComponents: (scenario as any).vmtReductionComponents || {
            transitOrientedDevelopment: 0,
            activeTransportation: 0,
            transitImprovements: 0,
          },
          parkingPricing: (scenario as any).parkingPricing || { priceIncreasePercent: 0, vmtAffectedPercent: 0, commuteByCarPercent: 0, basePricePerHour: 0 },
          micromobility: (scenario as any).micromobility || { scalePercent: 0, tripsPerResident: 0, percentBike: 0, percentScooter: 0, milesPerBikeTrip: 0, milesPerScooterTrip: 0, percentBikeTripsDisplaceCars: 0, percentScooterTripsDisplaceCars: 0, kwhPerMileBike: 0, kwhPerMileScooter: 0, yearImplemented: 2025 },
        } as Partial<PolicyScenario>;

        // BAU (no VMT reduction, no added EV beyond current, all other policy levers neutral)
        const bauScenario: PolicyScenario = {
          ...scenario,
          ...zeroedPolicies,
          vmtReductionPercent: 0,
          evAdoptionPercent: results?.baseline.currentEVPercent || 0,
        } as PolicyScenario;
        const baseline = getCachedBaseline(baselineCacheKeyFor(location, emfacVersion, buildingsBauScenario));
        
        // Helper function to get time series data (synchronous if baseline cached, async otherwise)
        const getTimeSeriesData = async (scenario: PolicyScenario, cacheKey: string) => {
          // Check old cache first
          const cached = timeSeriesCache.get(cacheKey);
          if (cached) return cached;
          
          // If we have a baseline, apply transformations synchronously
          if (baseline) {
            const transformOptions = {
              ...(buildingsRngPercent != null ? { rngOverridePercent: buildingsRngPercent } : {}),
              ...(buildingsCop != null ? { cop: buildingsCop } : {}),
              ...(efficiencyLayerOptions ? { efficiencyLayerOptions } : {}),
            };
            const transformedProjections = applyPolicyTransformations(baseline, scenario, Object.keys(transformOptions).length > 0 ? transformOptions : undefined);
            const historicalUpTo2022 = baseline.historicalData.filter((d: TimeSeriesEmissionData) => d.year < 2023);
            const baselineData = [...historicalUpTo2022, ...baseline.baselineProjections];
            const projectedWithPolicies = [...historicalUpTo2022, ...transformedProjections];
            const result = {
              baselineData,
              projectedData: projectedWithPolicies,
              location
            };
            timeSeriesCache.set(cacheKey, result);
            return result;
          }
          
          // No baseline - calculate asynchronously
          const result = await generatePolicyTimeSeriesData(location, scenario, emfacVersion, buildingsBauScenario, tsGenOptions);
          timeSeriesCache.set(cacheKey, result);
          return result;
        };

        const bauKey = createScenarioKey(location, bauScenario, emfacVersion);
        const bau = await getTimeSeriesData(bauScenario, bauKey);

        // VMT only
        const vmtOnlyScenario: PolicyScenario = {
          ...scenario,
          ...zeroedPolicies,
          vmtReductionPercent: scenario.vmtReductionPercent || 0,
          evAdoptionPercent: results?.baseline.currentEVPercent || 0,
        } as PolicyScenario;
        const vmtOnlyKey = createScenarioKey(location, vmtOnlyScenario, emfacVersion);
        const vmtOnly = await getTimeSeriesData(vmtOnlyScenario, vmtOnlyKey);

        // With both (current scenario)
        const bothScenario: PolicyScenario = {
          ...scenario,
          heatPumpAdoptionPercent: scenario.heatPumpAdoptionPercent || 0,
          solarCapacityMultiplier: scenario.solarCapacityMultiplier || 1,
          hdvElectrificationPercent: scenario.hdvElectrificationPercent || 0,
        } as PolicyScenario;
        const bothKey = createScenarioKey(location, bothScenario, emfacVersion);
        const both = await getTimeSeriesData(bothScenario, bothKey);

        // Line charts start at baseline year (2023); include last baseline year then projected
        const lastBaseline = bau.baselineData[bau.baselineData.length - 1];
        const yrs = lastBaseline && lastBaseline.year >= LINE_CHART_START_YEAR
          ? [lastBaseline.year, ...bau.projectedData.map(r => r.year)]
          : bau.projectedData.map(r => r.year).filter((y) => y >= LINE_CHART_START_YEAR);
        const sumCore = (row: TimeSeriesEmissionData) => (row.lightDutyEmissions + row.heavyDutyEmissions + row.naturalGasEmissions + row.electricityEmissions);

        // Compute "Other" baseline (everything not LDV/HDV/NG/Electricity) using current results baseline
        const coreBaseline =
          (results?.baseline.lightDutyGHG || 0) +
          (results?.baseline.heavyDutyGHG || 0) +
          (results?.baseline.naturalGasGHG || 0) +
          (timeSeriesData?.baselineData?.[timeSeriesData.baselineData.length - 1]?.electricityEmissions || 0);
        const otherBaseline = Math.max(0, (results?.baseline.totalGHG || 0) - coreBaseline);

        // Fetch population series and project to requested years (fallback: flat)
        let popByYear: Record<number, number> = {};
        try {
          const { data: popRows } = await supabaseClient
            .from('population')
            .select('year, population')
            .eq('location_name', location)
            .order('year', { ascending: true });
          if (popRows && popRows.length > 0) {
            popRows.forEach((r: any) => { popByYear[r.year] = r.population; });
            // Simple linear extrapolation to 2045 if needed
            const yearsAvail = Object.keys(popByYear).map(y => parseInt(y)).sort((a,b)=>a-b);
            const lastYear = yearsAvail[yearsAvail.length - 1];
            const prevYear = yearsAvail[yearsAvail.length - 2] || lastYear - 1;
            const growth = lastYear > prevYear ? (popByYear[lastYear] - popByYear[prevYear]) / (lastYear - prevYear) : 0;
            const neededYears = yrs.filter(y => !(y in popByYear));
            neededYears.forEach(y => { popByYear[y] = popByYear[lastYear] + growth * (y - lastYear); });
          }
        } catch (e) {
          // Population lookup failed; using flat other emissions
        }
        const basePop = popByYear[CHART_BASELINE_YEAR] || popByYear[Object.keys(popByYear).map(n=>parseInt(n)).sort((a,b)=>b-a)[0]] || 1;
        const otherByYear = yrs.map(y => Math.round(otherBaseline * (popByYear[y] ? popByYear[y] / basePop : 1)));

        const sumTotals = (rows: TimeSeriesEmissionData[]) => rows.map((r, i) => Math.round(sumCore(r) + (otherByYear[i] || 0)));

        const bauRows = lastBaseline && lastBaseline.year >= LINE_CHART_START_YEAR
          ? [lastBaseline, ...bau.projectedData]
          : bau.projectedData.filter((r) => r.year >= LINE_CHART_START_YEAR);
        const vmtOnlyRows = lastBaseline && lastBaseline.year >= LINE_CHART_START_YEAR
          ? [vmtOnly.baselineData[vmtOnly.baselineData.length - 1], ...vmtOnly.projectedData]
          : vmtOnly.projectedData.filter((r) => r.year >= LINE_CHART_START_YEAR);
        const bothRows = lastBaseline && lastBaseline.year >= LINE_CHART_START_YEAR
          ? [both.baselineData[both.baselineData.length - 1], ...both.projectedData]
          : both.projectedData.filter((r) => r.year >= LINE_CHART_START_YEAR);
        const bauTotals = sumTotals(bauRows);
        const vmtOnlyTotals = sumTotals(vmtOnlyRows);
        const bothTotals = sumTotals(bothRows);

        // Construct wedges for forecast horizon
        const vmtWedge = bauTotals.map((v, i) => Math.max(0, v - vmtOnlyTotals[i]));
        const evWedge = vmtOnlyTotals.map((v, i) => Math.max(0, v - bothTotals[i]));

        const forecastResult = { years: yrs, bauTotals, withPoliciesTotals: bothTotals, vmtWedge, evWedge };
        
        // Cache the result
        forecastCache.current.set(forecastCacheKey, forecastResult);
        // Limit cache size
        if (forecastCache.current.size > 10) {
          const firstKey = forecastCache.current.keys().next().value;
          if (firstKey) {
            forecastCache.current.delete(firstKey);
          }
        }
        
        if (!cancelled) setForecastData(forecastResult);
      } catch (e) {
        if (!cancelled) setForecastData(null);
      }
    };
    void buildForecast();
    return () => {
      cancelled = true;
    };
  }, [
    chartType,
    location,
    scenarioKey,
    results?.baseline.currentEVPercent,
    emfacVersion,
    results,
    scenario,
    timeSeriesData?.baselineData,
    tsGenOptions,
    buildingsBauScenario,
    buildingsCop,
    buildingsRngPercent,
  ]);

  // Memoize chart data preparation to avoid recalculation on every render
  // Must be called before any early returns (React Hooks rule)
  const chartData = useMemo(() => {
    if (!timeSeriesData) return null;

    // Combine baseline and projected data; line charts start at baseline year (2023)
    const allYears = [...timeSeriesData.baselineData.map(d => d.year), ...timeSeriesData.projectedData.map(d => d.year)];
    const uniqueYears = Array.from(new Set(allYears)).sort((a, b) => a - b).filter((y) => y >= LINE_CHART_START_YEAR);
    
    // Create maps for easy lookup
    const baselineMap = new Map(timeSeriesData.baselineData.map(d => [d.year, d]));
    const projectedMap = new Map(timeSeriesData.projectedData.map(d => [d.year, d]));
    
    // Line chart shows key categories with baseline vs with-policies.
    const baselineData = {
      lightDuty: uniqueYears.map((year) => baselineMap.get(year)?.lightDutyEmissions ?? null),
      naturalGas: uniqueYears.map((year) => baselineMap.get(year)?.naturalGasEmissions ?? null),
      electricity: uniqueYears.map((year) => baselineMap.get(year)?.electricityEmissions ?? null),
      waste: uniqueYears.map((year) => baselineMap.get(year)?.wasteEmissions ?? null),
      agriculture: uniqueYears.map((year) => baselineMap.get(year)?.agricultureEmissions ?? null),
    };

    // Show "With Policies" series starting at the baseline year (2023).
    // 2023 itself is unchanged by policies, but including it avoids a visual jump
    // and makes the dotted series clearly start from the baseline.
    const projectedData = {
      lightDuty: uniqueYears.map((year) =>
        year >= CHART_BASELINE_YEAR ? (projectedMap.get(year)?.lightDutyEmissions ?? null) : null
      ),
      naturalGas: uniqueYears.map((year) =>
        year >= CHART_BASELINE_YEAR ? (projectedMap.get(year)?.naturalGasEmissions ?? null) : null
      ),
      electricity: uniqueYears.map((year) =>
        year >= CHART_BASELINE_YEAR ? (projectedMap.get(year)?.electricityEmissions ?? null) : null
      ),
      waste: uniqueYears.map((year) =>
        year >= CHART_BASELINE_YEAR ? (projectedMap.get(year)?.wasteEmissions ?? null) : null
      ),
      agriculture: uniqueYears.map((year) =>
        year >= CHART_BASELINE_YEAR ? (projectedMap.get(year)?.agricultureEmissions ?? null) : null
      ),
    };

    return {
    labels: uniqueYears.map((y) => y.toString()),
    datasets: [
      {
        label: 'LDV (Baseline)',
        data: baselineData.lightDuty,
        backgroundColor: 'rgba(59, 130, 246, 0.3)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 4,
        fill: false,
          tension: 0,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
      {
        label: 'LDV',
        data: projectedData.lightDuty,
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 5,
        borderDash: [5, 5],
        fill: false,
          tension: 0,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
      {
        label: 'Natural Gas (Baseline)',
        data: baselineData.naturalGas,
        backgroundColor: POLICY_TIMESERIES_BUILDING_EMISSION_COLORS.naturalGas.fillSoft,
        borderColor: POLICY_TIMESERIES_BUILDING_EMISSION_COLORS.naturalGas.border,
        borderWidth: 4,
        fill: false,
          tension: 0,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
      {
        label: 'Natural Gas',
        data: projectedData.naturalGas,
        backgroundColor: POLICY_TIMESERIES_BUILDING_EMISSION_COLORS.naturalGas.fill,
        borderColor: POLICY_TIMESERIES_BUILDING_EMISSION_COLORS.naturalGas.border,
        borderWidth: 5,
        borderDash: [5, 5],
        fill: false,
          tension: 0,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
      {
        label: 'Electricity (Baseline)',
        data: baselineData.electricity,
        backgroundColor: POLICY_TIMESERIES_BUILDING_EMISSION_COLORS.electricity.fillSoft,
        borderColor: POLICY_TIMESERIES_BUILDING_EMISSION_COLORS.electricity.border,
        borderWidth: 4,
        fill: false,
          tension: 0,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
      {
        label: 'Electricity',
        data: projectedData.electricity,
        backgroundColor: POLICY_TIMESERIES_BUILDING_EMISSION_COLORS.electricity.fill,
        borderColor: POLICY_TIMESERIES_BUILDING_EMISSION_COLORS.electricity.border,
        borderWidth: 5,
        borderDash: [5, 5],
        fill: false,
          tension: 0,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
      {
        label: 'Waste (Baseline)',
        data: baselineData.waste,
        backgroundColor: 'rgba(236, 72, 153, 0.3)',
        borderColor: 'rgba(236, 72, 153, 1)',
        borderWidth: 4,
        fill: false,
          tension: 0,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
      {
        label: 'Waste',
        data: projectedData.waste,
        backgroundColor: 'rgba(236, 72, 153, 0.8)',
        borderColor: 'rgba(236, 72, 153, 1)',
        borderWidth: 5,
        borderDash: [5, 5],
        fill: false,
          tension: 0,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
      {
        label: 'Agriculture (Baseline)',
        data: baselineData.agriculture,
        backgroundColor: 'rgba(22, 101, 52, 0.35)',
        borderColor: 'rgba(22, 101, 52, 1)',
        borderWidth: 4,
        fill: false,
        tension: 0,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
      {
        label: 'Agriculture',
        data: projectedData.agriculture,
        backgroundColor: 'rgba(22, 101, 52, 0.85)',
        borderColor: 'rgba(22, 101, 52, 1)',
        borderWidth: 5,
        borderDash: [5, 5],
        fill: false,
        tension: 0,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
    ],
  };
  }, [timeSeriesData]);

  // Feed PolicyImpactTool metric cards from the same totals used by the stacked chart.
  const computedTotals = useMemo(() => {
    if (!timeSeriesData) return null;
    const baselineYear = timeSeriesData.baselineData.some((d) => d.year === 2023)
      ? 2023
      : timeSeriesData.baselineData[timeSeriesData.baselineData.length - 1]?.year;
    const projectedYear = timeSeriesData.projectedData.some((d) => d.year === 2045)
      ? 2045
      : timeSeriesData.projectedData[timeSeriesData.projectedData.length - 1]?.year;
    if (!baselineYear || !projectedYear) return null;

    const baselineRow = timeSeriesData.baselineData.find((d) => d.year === baselineYear);
    const projectedRow = timeSeriesData.projectedData.find((d) => d.year === projectedYear);
    if (!baselineRow || !projectedRow) return null;

    const baselineTotal = baselineRow.totalEmissions || 0;
    const projectedWithPoliciesTotal = projectedRow.totalEmissions || 0;
    const totalReduction = baselineTotal - projectedWithPoliciesTotal;
    const percentReduction = baselineTotal > 0 ? (totalReduction / baselineTotal) * 100 : 0;

    return {
      baselineYear,
      baselineTotal,
      projectedYear,
      projectedWithPoliciesTotal,
      totalReduction,
      percentReduction,
    };
  }, [timeSeriesData]);

  useEffect(() => {
    if (onTotalsChange && computedTotals) onTotalsChange(computedTotals);
  }, [onTotalsChange, computedTotals]);

  // Line-chart y-axis max (exclude the vertical separator's synthetic spike)
  // Must be declared before any early returns (React Hooks rule)
  const lineChartRange = useMemo(() => {
    if (!timeSeriesData) return { min: 0, max: 0 };
    // Line chart plots key sector series, so compute min/max across those values.
    const rawValues = [
      ...timeSeriesData.baselineData.flatMap((d) => [
        d.lightDutyEmissions,
        d.naturalGasEmissions,
        d.electricityEmissions,
        d.wasteEmissions,
        d.agricultureEmissions,
      ]),
      ...timeSeriesData.projectedData.flatMap((d) => [
        d.lightDutyEmissions,
        d.naturalGasEmissions,
        d.electricityEmissions,
        d.wasteEmissions,
        d.agricultureEmissions,
      ]),
    ].filter((v) => typeof v === 'number' && Number.isFinite(v)) as number[];

    if (!rawValues.length) return { min: 0, max: 0 };

    let min = Math.min(...rawValues);
    let max = Math.max(...rawValues);

    if (perCapitaMode && results?.baseline?.population && results.baseline.population > 0) {
      const pop = results.baseline.population;
      min = min / pop;
      max = max / pop;
    }

    return { min, max };
  }, [timeSeriesData, perCapitaMode, results?.baseline?.population]);

  const lineChartYAxis = useMemo(() => {
    const { max } = lineChartRange;
    if (!Number.isFinite(max) || max === 0) {
      return { min: 0, max: undefined as number | undefined, stepSize: undefined as number | undefined };
    }
    const { max: niceMax, stepSize } = getNiceAxisMaxAndStep(max, 6);
    const maxAllowed = max * 1.5;
    const finalMax = Math.min(niceMax, maxAllowed);
    return { min: 0, max: finalMax, stepSize };
  }, [lineChartRange]);

  // Stacked area chart data - only "With Policies" for LDV, Natural Gas, and Electricity
  const stackedChartData = useMemo(() => {
    if (!timeSeriesData) return null;

    // Line/stacked charts start at baseline year (2023)
    const allYears = [...timeSeriesData.baselineData.map(d => d.year), ...timeSeriesData.projectedData.map(d => d.year)];
    const uniqueYears = Array.from(new Set(allYears)).sort((a, b) => a - b).filter((y) => y >= LINE_CHART_START_YEAR);
    
    // Create maps for easy lookup
    const baselineMap = new Map(timeSeriesData.baselineData.map(d => [d.year, d]));
    const projectedMap = new Map(timeSeriesData.projectedData.map(d => [d.year, d]));
    
    // Get "With Policies" data for the three categories
    const projectedData = {
      // Use nullish coalescing so real zeros remain zeros (important for clean-electricity-year ramps).
      lightDuty: uniqueYears.map(year => projectedMap.get(year)?.lightDutyEmissions ?? null),
      naturalGas: uniqueYears.map(year => projectedMap.get(year)?.naturalGasEmissions ?? null),
      electricity: uniqueYears.map(year => projectedMap.get(year)?.electricityEmissions ?? null),
      waste: uniqueYears.map(year => projectedMap.get(year)?.wasteEmissions ?? null),
      agriculture: uniqueYears.map(year => projectedMap.get(year)?.agricultureEmissions ?? null),
      heavyDuty: uniqueYears.map(year => projectedMap.get(year)?.heavyDutyEmissions ?? null),
      offroad: uniqueYears.map(year => projectedMap.get(year)?.offroadEmissions ?? null),
    };

    // Calculate target line value based on baseline year
    // NOTE: baselineData may not contain the selected baseline year (e.g. 2005),
    // so fall back to projectedMap. Also, this stacked chart only shows LDV+NG+Electricity,
    // so compute target on the same scope to keep it visible on the same y-scale.
    const baselineYearData = baselineMap.get(targetBaselineYear) ?? projectedMap.get(targetBaselineYear);
    const baselineYearTotal = baselineYearData
      ? baselineYearData.lightDutyEmissions +
        baselineYearData.naturalGasEmissions +
        baselineYearData.electricityEmissions +
        (baselineYearData as any).wasteEmissions +
        (baselineYearData.agricultureEmissions || 0) +
        baselineYearData.heavyDutyEmissions +
        (baselineYearData as any).offroadEmissions
      : 0;
    const refYear = targetBaselineYear;
    const goalYear = targetEndYear;
    const vRef = baselineYearTotal;
    const vGoal = baselineYearTotal * (1 - targetReductionPercent / 100);

    const yLo = Math.min(refYear, goalYear);
    const yHi = Math.max(refYear, goalYear);
    const span = goalYear - refYear;

    const targetLineData = uniqueYears.map((year) => {
      if (year < yLo || year > yHi) return null;
      if (span === 0) return year === refYear ? vGoal : null;
      const t = (year - refYear) / span;
      return vRef + t * (vGoal - vRef);
    });

    // Calculate sum of stacked areas (With Policies) for each year
    const stackedAreasTotal = uniqueYears.map((year, index) => {
      const lightDuty = projectedData.lightDuty[index];
      const heavyDuty = projectedData.heavyDuty[index];
      const naturalGas = projectedData.naturalGas[index];
      const electricity = projectedData.electricity[index];
      const waste = projectedData.waste[index];
      const agriculture = projectedData.agriculture[index];
      const offroad = projectedData.offroad[index];
      
      const sum = (lightDuty || 0) + (heavyDuty || 0) + (naturalGas || 0) + 
                  (electricity || 0) + (waste || 0) + (agriculture || 0) + (offroad || 0);
      return sum > 0 ? sum : null;
    });

    // Baseline total line: sum of all emissions before policies
    // For historical years: use baselineData
    // For projected years: baseline projections should be in baselineData, but if missing, 
    // we need to calculate from baseline projections (not from "with policies" data)
    const baselineTotalData = uniqueYears.map((year, index) => {
      const baselineRow = baselineMap.get(year);
      if (baselineRow) {
        // We have baseline data for this year - sum all sources
        const baselineSum = (baselineRow.lightDutyEmissions || 0) +
               (baselineRow.heavyDutyEmissions || 0) +
               (baselineRow.naturalGasEmissions || 0) +
               (baselineRow.electricityEmissions || 0) +
               (baselineRow.wasteEmissions || 0) +
               (baselineRow.agricultureEmissions || 0) +
               (baselineRow.offroadEmissions || 0);
        return baselineSum;
      }
      // For projected years without baseline data, we should not fall back to stacked areas
      // Instead, return null so the line doesn't extend incorrectly
      // The baseline projections should be in baselineData, so this shouldn't happen
      return null;
    });

    // Calculate Reductions wedge: difference between baseline total and stacked areas total
    // Show from 2023 onwards (reductions start from baseline year)
    // The value represents how much to stack on top of the stacked areas to reach the baseline
    const reductionsData = uniqueYears.map((year, index) => {
      // Use 0 instead of null for years before 2023 to prevent Chart.js from drawing lines
      if (year < 2023) return 0; // No reductions before 2023 - use 0 to prevent line extension
      const baseline = baselineTotalData[index];
      const stacked = stackedAreasTotal[index];
      if (baseline === null || stacked === null) return 0;
      
      // Exact reductions wedge = Baseline total - With Policies total (clamped at 0 so we don't render negative wedges).
      return Math.max(0, baseline - stacked);
    });

    // If per-capita mode is enabled, divide all emission series by (population in that year)
    // using population from Supabase. We approximate using latest population scaled by inventory ratios.
    if (perCapitaMode && results?.baseline?.population) {
      const basePop = results.baseline.population || 1;
      // Build a year->population map from Supabase population table
      // We cannot make async calls inside useMemo, so we approximate using latest baseline population
      // and assume flat population over time for now.
      const populationByYear: Record<number, number> = {};
      uniqueYears.forEach((y) => {
        populationByYear[y] = basePop;
      });

      const divideSeries = (data: (number | null)[]) =>
        data.map((val, idx) => {
          if (val == null) return 0;
          const year = uniqueYears[idx];
          const pop = populationByYear[year] || basePop;
          return pop > 0 ? val / pop : 0;
        });

      projectedData.lightDuty = divideSeries(projectedData.lightDuty);
      projectedData.heavyDuty = divideSeries(projectedData.heavyDuty);
      projectedData.naturalGas = divideSeries(projectedData.naturalGas);
      projectedData.electricity = divideSeries(projectedData.electricity);
      projectedData.waste = divideSeries(projectedData.waste);
      projectedData.offroad = divideSeries(projectedData.offroad);

      stackedAreasTotal.splice(
        0,
        stackedAreasTotal.length,
        ...stackedAreasTotal.map((val, idx) => {
          if (val == null) return 0;
          const year = uniqueYears[idx];
          const pop = populationByYear[year] || basePop;
          return pop > 0 ? val / pop : 0;
        })
      );

      for (let i = 0; i < baselineTotalData.length; i++) {
        const val = baselineTotalData[i];
        if (val == null) continue;
        const year = uniqueYears[i];
        const pop = populationByYear[year] || basePop;
        baselineTotalData[i] = pop > 0 ? val / pop : 0;
      }

      for (let i = 0; i < reductionsData.length; i++) {
        const val = reductionsData[i];
        if (val == null) continue;
        const year = uniqueYears[i];
        const pop = populationByYear[year] || basePop;
        reductionsData[i] = pop > 0 ? val / pop : 0;
      }

      for (let i = 0; i < targetLineData.length; i++) {
        const val = targetLineData[i];
        const year = uniqueYears[i];
        const pop = populationByYear[year] || basePop;
        targetLineData[i] = pop > 0 ? val / pop : 0;
      }
    }

    const datasets: any[] = [
        // Stacked areas in order from bottom to top (for legend alignment)
        {
          label: 'LDV',
          data: projectedData.lightDuty,
          backgroundColor: 'rgba(59, 130, 246, 0.7)',
          borderColor: 'transparent',
          borderWidth: 0,
          fill: true,
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 5,
          stack: 'main',
          order: 0, // Bottom of stack (match GHG Inventory ordering)
        },
        {
          label: 'HDV',
          data: projectedData.heavyDuty,
          backgroundColor: 'rgba(96, 165, 250, 0.7)',
          borderColor: 'transparent',
          borderWidth: 0,
          fill: true,
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 5,
          stack: 'main',
          order: 1,
        },
        {
          label: 'Natural Gas',
          data: projectedData.naturalGas,
          backgroundColor: POLICY_TIMESERIES_BUILDING_EMISSION_COLORS.naturalGas.stackedBar,
          borderColor: 'transparent',
          borderWidth: 0,
          fill: true,
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 5,
          stack: 'main',
          order: 2,
        },
        {
          label: 'Electricity',
          data: projectedData.electricity,
          backgroundColor: POLICY_TIMESERIES_BUILDING_EMISSION_COLORS.electricity.stackedBar,
          borderColor: 'transparent',
          borderWidth: 0,
          fill: true,
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 5,
          stack: 'main',
          order: 3,
        },
        {
          label: 'Waste',
          data: projectedData.waste,
          backgroundColor: 'rgba(236, 72, 153, 0.7)',
          borderColor: 'transparent',
          borderWidth: 0,
          fill: true,
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 5,
          stack: 'main',
          order: 4,
        },
        {
          label: 'Agriculture',
          data: projectedData.agriculture,
          backgroundColor: 'rgba(22, 163, 74, 0.72)',
          borderColor: 'transparent',
          borderWidth: 0,
          fill: true,
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 5,
          stack: 'main',
          order: 5,
        },
        {
          label: 'Offroad',
          data: projectedData.offroad,
          backgroundColor: 'rgba(255, 210, 0, 0.65)',
          borderColor: 'transparent',
          borderWidth: 0,
          fill: true,
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 5,
          stack: 'main',
          order: 6,
        },
        {
          label: 'Reductions',
          data: reductionsData,
          backgroundColor: 'rgba(107, 114, 128, 0.7)', // Gray fill - solid color for independent wedge
          borderColor: 'transparent',
          borderWidth: 0,
          fill: true,
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 5,
          stack: 'main', // Stack on top of other areas - this creates the wedge between stacked areas and baseline
          order: 7, // After all other stacked areas
          spanGaps: false, // Don't connect through zero values
        },
        {
          label: 'Total (Baseline)',
          data: baselineTotalData,
          yAxisID: 'yTotals',
          backgroundColor: 'rgba(0, 0, 0, 0)',
          borderColor: 'rgba(107, 114, 128, 1)', // Gray color instead of black
          borderWidth: 2,
          fill: false,
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 6,
          order: 10, // Draw on top of stacked areas and reductions wedge
          spanGaps: false,
        },
    ];

    if (!perCapitaMode) {
      datasets.push({
        label: 'Target',
        data: targetLineData,
        yAxisID: 'yTotals',
        backgroundColor: 'transparent',
        /* Visible stroke is drawn by targetTrajectoryOnTop (afterDraw). */
        borderColor: 'rgba(0, 0, 0, 0)',
        borderWidth: 2,
        fill: false,
        tension: 0,
        pointRadius: 0,
        pointHoverRadius: 5,
        borderDash: [10, 5],
        order: 99,
        spanGaps: false,
      });
    }

    return {
      labels: uniqueYears.map((y) => y.toString()),
      datasets,
    };
  }, [
    timeSeriesData,
    targetReductionPercent,
    targetBaselineYear,
    targetEndYear,
    perCapitaMode,
    results?.baseline?.population,
  ]);

  /** vRef / vGoal for y-axis range (linear target path endpoints). */
  const stackedChartTargetExtents = useMemo(() => {
    if (!timeSeriesData) return null;
    const baselineMap = new Map(timeSeriesData.baselineData.map(d => [d.year, d]));
    const projectedMap = new Map(timeSeriesData.projectedData.map(d => [d.year, d]));
    const baselineYearData = baselineMap.get(targetBaselineYear) ?? projectedMap.get(targetBaselineYear);
    const baselineYearTotal = baselineYearData
      ? baselineYearData.lightDutyEmissions +
        baselineYearData.naturalGasEmissions +
        baselineYearData.electricityEmissions +
        (baselineYearData as any).wasteEmissions +
        (baselineYearData.agricultureEmissions || 0) +
        baselineYearData.heavyDutyEmissions +
        (baselineYearData as any).offroadEmissions
      : 0;
    const vRef = baselineYearTotal;
    const vGoal = baselineYearTotal * (1 - targetReductionPercent / 100);
    return { vRef, vGoal };
  }, [timeSeriesData, targetBaselineYear, targetReductionPercent]);

  // Calculate max value for stacked chart y-axis (sum of all components for each year)
  const stackedChartRange = useMemo(() => {
    if (!timeSeriesData) return { min: undefined as number | undefined, max: undefined as number | undefined };
    const projectedMap = new Map(timeSeriesData.projectedData.map(d => [d.year, d]));
    const baselineMap = new Map(timeSeriesData.baselineData.map(d => [d.year, d]));
    
    // Get all years
    const allYears = [...timeSeriesData.baselineData.map(d => d.year), ...timeSeriesData.projectedData.map(d => d.year)];
    const uniqueYears = Array.from(new Set(allYears)).sort((a, b) => a - b);
    
    // Calculate total for each year (sum of all components)
    const totals = uniqueYears.map(year => {
      // Use projected data (With Policies) for the sum
      const projectedRow = projectedMap.get(year);
      if (projectedRow) {
        return (projectedRow.lightDutyEmissions || 0) +
               (projectedRow.heavyDutyEmissions || 0) +
               (projectedRow.naturalGasEmissions || 0) +
               (projectedRow.electricityEmissions || 0) +
               (projectedRow.wasteEmissions || 0) +
               (projectedRow.agricultureEmissions || 0) +
               (projectedRow.offroadEmissions || 0);
      }
      // Fallback to baseline if projected not available
      const baselineRow = baselineMap.get(year);
      if (baselineRow) {
        return (baselineRow.lightDutyEmissions || 0) +
               (baselineRow.heavyDutyEmissions || 0) +
               (baselineRow.naturalGasEmissions || 0) +
               (baselineRow.electricityEmissions || 0) +
               (baselineRow.wasteEmissions || 0) +
               (baselineRow.agricultureEmissions || 0) +
               (baselineRow.offroadEmissions || 0);
      }
      return 0;
    }).filter(v => typeof v === 'number' && Number.isFinite(v) && v > 0) as number[];
    if (!totals.length) return { min: undefined as number | undefined, max: undefined as number | undefined };

    let min = Math.min(...totals);
    let max = Math.max(...totals);

    if (perCapitaMode && results?.baseline?.population && results.baseline.population > 0) {
      const pop = results.baseline.population;
      min = min / pop;
      max = max / pop;
    }

    return { min, max };
  }, [timeSeriesData, perCapitaMode, results?.baseline?.population]);

  /** Raw data max (MT) for stacked chart; used with target extents when not per-capita. */
  const stackedChartMaxValue = useMemo(() => {
    if (!timeSeriesData) return undefined;
    const projectedMap = new Map(timeSeriesData.projectedData.map((d) => [d.year, d]));
    const baselineMap = new Map(timeSeriesData.baselineData.map((d) => [d.year, d]));
    const allYears = [
      ...timeSeriesData.baselineData.map((d) => d.year),
      ...timeSeriesData.projectedData.map((d) => d.year),
    ];
    const uniqueYears = Array.from(new Set(allYears)).sort((a, b) => a - b);
    const totals = uniqueYears
      .map((year) => {
        const projectedRow = projectedMap.get(year);
        if (projectedRow) {
          return (
            (projectedRow.lightDutyEmissions || 0) +
            (projectedRow.heavyDutyEmissions || 0) +
            (projectedRow.naturalGasEmissions || 0) +
            (projectedRow.electricityEmissions || 0) +
            (projectedRow.wasteEmissions || 0) +
            (projectedRow.agricultureEmissions || 0) +
            (projectedRow.offroadEmissions || 0)
          );
        }
        const baselineRow = baselineMap.get(year);
        if (baselineRow) {
          return (
            (baselineRow.lightDutyEmissions || 0) +
            (baselineRow.heavyDutyEmissions || 0) +
            (baselineRow.naturalGasEmissions || 0) +
            (baselineRow.electricityEmissions || 0) +
            (baselineRow.wasteEmissions || 0) +
            (baselineRow.agricultureEmissions || 0) +
            (baselineRow.offroadEmissions || 0)
          );
        }
        return 0;
      })
      .filter((v) => typeof v === 'number' && Number.isFinite(v) && v > 0) as number[];
    return totals.length ? Math.max(...totals) : undefined;
  }, [timeSeriesData]);

  const stackedChartYAxis = useMemo(() => {
    if (perCapitaMode) {
      const { max } = stackedChartRange;
      if (max === undefined) {
        return {
          min: 0 as number | undefined,
          max: undefined as number | undefined,
          stepSize: undefined as number | undefined,
        };
      }
      const { max: niceMax, stepSize } = getNiceAxisMaxAndStep(max, 6);
      const maxAllowed = max * 1.5;
      const finalMax = Math.min(niceMax, maxAllowed);
      return { min: 0, max: finalMax, stepSize };
    }
    if (stackedChartMaxValue === undefined) {
      return { min: 0, max: undefined as number | undefined, stepSize: undefined as number | undefined };
    }
    const ext = stackedChartTargetExtents;
    const targetPeak = ext ? Math.max(ext.vRef, ext.vGoal, 0) : 0;
    const maxValue = Math.max(stackedChartMaxValue, targetPeak);
    const { max, stepSize } = getNiceAxisMaxAndStep(maxValue, 6);
    return { min: 0, max: max + stepSize * 0.1, stepSize };
  }, [perCapitaMode, stackedChartRange, stackedChartMaxValue, stackedChartTargetExtents]);

  // Stacked chart options
  const stackedChartOptions = useMemo(() => {
    return {
      type: 'line' as const,
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          top: 10, // Reduced since legend is on the right
          bottom: 16, // Reduced to minimize white space below chart
          left: 0, // Remove left padding to move chart left
          right: 50, // Further reduced to make chart even wider
        },
      },
      interaction: {
        intersect: false,
        mode: 'index' as const,
      },
      plugins: {
        // Opt-in for the global verticalSeparator plugin (Policy Tool only)
        verticalSeparator: { enabled: true },
        targetTrajectoryOnTop: { enabled: true },
        legend: {
          position: 'right' as const,
          align: 'center' as const, // Center legend vertically in available space
          labels: {
            usePointStyle: false,
            padding: 6, // Reduced padding between legend items
            boxWidth: 24,
            boxHeight: 12,
            font: {
              size: 13, // Increased legend font size
            },
            generateLabels: function(chart: any) {
              const original = Chart.defaults.plugins.legend.labels.generateLabels;
              const labels = original.call(this, chart);

              labels.forEach((label: any) => {
                const idx = label.datasetIndex;
                if (idx === undefined) return;
                const ds = chart.data.datasets[idx];
                if (!ds) return;
                const dash = ds.borderDash;
                label.lineDash = Array.isArray(dash) && dash.length > 0 ? [...dash] : [];
                const bc = ds.borderColor;
                if (typeof bc === 'string' && bc !== 'transparent') {
                  label.strokeStyle = bc;
                  label.fillStyle = bc;
                }
              });

              // Reorder labels to match chart order from bottom to top
              // Chart visual order (bottom to top): Target (line), LDV (0), HDV (1), Natural Gas (2), Electricity (3), Waste (4), Offroad (5), Reductions (6), Total Baseline (line)
              // Legend order (reading bottom to top): Target, LDV, HDV, Natural Gas, Electricity, Waste, Offroad, Reductions, Total Baseline
              // With sort orderA - orderB: lower numbers appear first in array (top of legend), higher numbers appear last (bottom of legend)
              // So we need: Target (highest order, bottom), then stack items in reverse (LDV highest, Reductions lowest), then Total Baseline (lowest order, top)
              const orderMap = new Map<string, number>();
              chart.data.datasets.forEach((dataset: any) => {
                if (dataset.stack === 'main') {
                  // Reverse the order: LDV (0) → 6, HDV (1) → 5, Natural Gas (2) → 4, etc.
                  // This makes LDV appear right after Target when reading bottom-up
                  const originalOrder = dataset.order ?? 999;
                  orderMap.set(dataset.label, 6 - originalOrder);
                } else if (dataset.label === 'Total (Baseline)') {
                  orderMap.set(dataset.label, -1); // Lowest order so it appears at TOP of legend
                } else if (dataset.label === 'Target') {
                  orderMap.set(dataset.label, 10); // Highest order so it appears at BOTTOM of legend
                } else {
                  orderMap.set(dataset.label, 999);
                }
              });

              // Sort: Total Baseline (-1) at top, then Reductions (0), Offroad (1), Waste (2), Electricity (3), Natural Gas (4), HDV (5), LDV (6), Target (10) at bottom
              // When reading legend from bottom to top: Target, LDV, HDV, Natural Gas, Electricity, Waste, Offroad, Reductions, Total Baseline ✓
              labels.sort((a: any, b: any) => {
                const orderA = orderMap.get(a.text) ?? 999;
                const orderB = orderMap.get(b.text) ?? 999;
                return orderA - orderB;
              });

              labels.forEach((label: any) => {
                const dataset = chart.data.datasets.find((d: any) => d.label === label.text);
                if (dataset?.label && dataset.label === 'Target') {
                  label.strokeStyle = 'rgba(0, 0, 0, 1)';
                  label.lineWidth = 1.5; // Reduced thickness for target line
                  label.fillStyle = 'rgba(0, 0, 0, 0)';
                  label.lineDash = [12, 6];
                }
              });

              return labels;
            },
          },
        },
        title: {
          display: false,
        },
        tooltip: {
          enabled: true,
          mode: 'index' as const,
          intersect: false,
          itemSort: (a: any, b: any) => {
            // Use the same order mapping as the legend
            const getOrder = (label: string) => {
              if (label === 'Total (Baseline)') return -1;
              if (label === 'Target') return 10;
              // For stacked items, reverse the order to match legend
              const dataset = (stackedChartData as any)?.datasets?.find((d: any) => d.label === label);
              if (dataset?.stack === 'main' && typeof dataset.order === 'number') {
                return 6 - dataset.order; // Reverse: LDV (0) → 6, HDV (1) → 5, etc.
              }
              return 999;
            };
            const orderA = getOrder(a.dataset.label || '');
            const orderB = getOrder(b.dataset.label || '');
            return orderA - orderB; // Same sort as legend
          },
          callbacks: {
            label: (context: any) => {
              const label = context.dataset.label || '';
              const value = context.parsed.y;
              if (typeof value !== 'number' || !Number.isFinite(value)) return '';
              return `${label}: ${value.toLocaleString()} MT CO₂e`;
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          type: 'category' as const,
          title: {
            display: false, // Removed "Year" label to save space
          },
          offset: false, // Remove offset to eliminate extra spacing
          bounds: 'data' as const, // Use exact data bounds to eliminate extra space
          min: undefined, // Let Chart.js determine from data
          max: undefined, // Let Chart.js determine from data
          grid: {
            display: true,
            offset: false, // Remove grid offset to align with data points
            color: (context: any) => {
              const label = context.tick?.label;
              if (label === '2023') {
                return 'rgba(107, 114, 128, 0.6)';
              }
              return 'rgba(0, 0, 0, 0.06)';
            },
            lineWidth: (context: any) => {
              const label = context.tick?.label;
              return label === '2023' ? 2 : 1;
            },
          },
          ticks: {
            display: true,
            autoSkip: false,
            maxTicksLimit: 50,
            callback: function(value: any, index: number, ticks: any[]) {
              const labels = (stackedChartData as any)?.labels || [];
              if (labels.length === 0) return '';
              const label = labels[index];
              const yearNum = parseInt(String(label), 10);
              if (Number.isNaN(yearNum)) return '';

              // Exclude 2023 and 2024
              if (yearNum === 2023 || yearNum === 2024) {
                return '';
              }

              // Always show first (2005) and last (2045) year
              if (index === 0 || index === labels.length - 1) {
                return String(yearNum);
              }

              // Show every 5 years
              if (yearNum % 5 === 0) {
                return String(yearNum);
              }
              return '';
            },
            maxRotation: 0,
            minRotation: 0,
            font: {
              size: 13, // Increased x-axis font size
            },
          },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          display: true,
          position: 'left' as const,
          // Set min/max based on actual data with reduced headroom
          min: stackedChartYAxis.min,
          max: stackedChartYAxis.max,
          title: {
            display: true,
            text: perCapitaMode ? 'metric tons CO₂e per person' : 'metric tons CO₂e',
            font: {
              size: 14, // Increased y-axis title font size
              weight: 'normal' as const,
              color: '#374151',
            },
            padding: {
              top: 0,
              bottom: 10,
              left: 0,
              right: 0,
            },
            color: '#374151',
          },
          ticks: {
            display: true,
            stepSize: stackedChartYAxis.stepSize,
            callback: function(value: any) {
              const numValue = typeof value === 'number' ? value : Number(value);
              if (Number.isNaN(numValue)) return '';
              return numValue.toLocaleString();
            },
            font: {
              size: 13, // Increased y-axis ticks font size
            },
            padding: 8,
            autoSkip: false,
            maxTicksLimit: 10,
            color: '#374151',
          },
          grid: {
            display: true,
            color: 'rgba(0, 0, 0, 0.1)',
            drawBorder: true,
            drawOnChartArea: true,
          },
        },
        // Absolute totals (baseline outline + target trajectory): must NOT use stacked y,
        // or Chart.js adds them on top of the area stack (inflated / wrong start year).
        yTotals: {
          type: 'linear' as const,
          stacked: false,
          display: false,
          position: 'left' as const,
          beginAtZero: true,
          max: stackedChartYAxis.max,
          min: 0,
          grid: {
            display: false,
            drawOnChartArea: false,
          },
          ticks: {
            display: false,
          },
        },
      },
    };
  }, [stackedChartData, stackedChartYAxis, perCapitaMode]);

  // Build forecast chart data/options when available
  // Must be called before any early returns (React Hooks rule)
  const forecastChartData = useMemo(() => {
    if (!forecastData) return null;
    
    return (() => {
    const bau = forecastData.bauTotals;
    const afterVmt = forecastData.bauTotals.map((v, i) => v - forecastData.vmtWedge[i]);
    const afterEv = afterVmt.map((v, i) => v - forecastData.evWedge[i]);

    // Get 2023 BAU value for flat line
    const year2023Index = forecastData.years.findIndex(y => y === 2023);
    const bau2023Value = year2023Index >= 0 ? bau[year2023Index] : null;
    
    // Create flat BAU line from 2023 to 2045
    const flatBauData = forecastData.years.map((year, index) => {
      if (year < 2023) return null;
      return bau2023Value;
    });

    return {
      labels: forecastData.years,
      datasets: [
        // BAU line with state policies (top reference) - must be first for VMT wedge fill target
        {
          label: 'BAU with state policies',
          data: bau,
          borderColor: 'rgba(31, 41, 55, 1)',
          backgroundColor: 'rgba(31, 41, 55, 0.1)',
          borderWidth: 4,
          pointRadius: 0,
          fill: false,
          tension: 0,
          order: 3,
        },
        // VMT wedge: area between BAU and After-VMT
        {
          label: 'VMT Reduction',
          data: afterVmt,
          borderColor: 'rgba(59, 130, 246, 0)',
          backgroundColor: 'rgba(59, 130, 246, 0.35)',
          pointRadius: 0,
          fill: {
            target: '-1', // fill to previous dataset (BAU with state policies)
          },
          tension: 0,
          order: 2,
        },
        // EV wedge: area between After-VMT and After-EV (With Policies)
        {
          label: 'Vehicle Electrification',
          data: afterEv,
          borderColor: 'rgba(96, 165, 250, 0)',
          backgroundColor: 'rgba(96, 165, 250, 0.35)',
          pointRadius: 0,
          fill: {
            target: '-1', // fill to previous dataset (After-VMT)
          },
          tension: 0,
          order: 1,
        },
        // With policies line (remaining emissions)
        {
          label: 'Total GHG',
          data: afterEv,
          borderColor: 'rgba(34, 197, 94, 1)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          borderWidth: 5,
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false,
          tension: 0,
          order: 4,
        },
        // Flat BAU line from 2023 to 2045 (drawn on top)
        {
          label: 'BAU',
          data: flatBauData,
          borderColor: 'rgba(107, 114, 128, 1)',
          backgroundColor: 'rgba(107, 114, 128, 0.1)',
          borderWidth: 3,
          pointRadius: 0,
          fill: false,
          tension: 0,
          order: 5,
        },
      ]
    };
    })();
  }, [forecastData]);

  // Early returns must come after all hooks
  // Show spinner while time series regenerates, or when parent recalculates impacts (e.g. EMFAC / Buildings BAU)
  // so users see feedback immediately instead of waiting for the debounced policy-impact effect.
  if (timeSeriesLoading || loading) {
    const progress: PolicyLoadProgress | null = loading ? loadProgress : timeSeriesProgress;
    const pct = progress && progress.stepCount > 0 ? Math.min(100, Math.round((100 * progress.stepIndex) / progress.stepCount)) : null;
    return (
      <div className="policy-impact-loading">
        <div className="loading-spinner"></div>
        <p className="policy-impact-loading-title">{loading ? 'Calculating policy impacts…' : 'Preparing emissions chart…'}</p>
        {progress && pct != null && (
          <div className="policy-impact-loading-detail" aria-live="polite">
            <div
              className="policy-impact-loading-progress-track"
              role="progressbar"
              aria-valuenow={progress.stepIndex}
              aria-valuemin={1}
              aria-valuemax={progress.stepCount}
              aria-label={`Loading step ${progress.stepIndex} of ${progress.stepCount}`}
            >
              <div className="policy-impact-loading-progress-fill" style={{ width: `${pct}%` }} />
            </div>
            <p className="policy-impact-loading-step">
              Step {progress.stepIndex} of {progress.stepCount}: {progress.label}
            </p>
          </div>
        )}
      </div>
    );
  }

  if (!timeSeriesData) {
    return (
      <div className="policy-impact-placeholder">
        <p></p>
      </div>
    );
  }

  // If results are not available yet, show a minimal placeholder
  // (but don't block if timeSeriesData is available, as chart can render with that)
  if (!results) {
    return (
      <div className="policy-impact-placeholder">
        <p>Loading baseline data...</p>
      </div>
    );
  }

  const forecastChartOptions = {
    type: 'line' as const,
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 20, // Reduced to minimize white space
        bottom: 16, // Reduced to minimize white space
        left: 80,
        right: 40,
      },
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: false,
          boxWidth: 28,
          generateLabels: legendLabelsReflectLineDash,
        },
      },
      tooltip: { intersect: false, mode: 'index' as const },
    },
    scales: {
      y: { title: { display: true, text: 'Emissions (Metric Tons CO₂e)' } },
      x: {
        title: { display: true, text: 'Year' },
        // Keep endpoints (e.g., 2005, 2045) fully visible
        offset: false,
      },
    },
  };

  const chartOptions = {
    type: 'line' as const,
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 20, // Reduced to minimize white space
        bottom: 16, // Reduced to minimize white space
        left: 90,   // room for Y-axis labels and title - increased to ensure visibility
        right: 20,
      },
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
    plugins: {
      // Opt-in for the global verticalSeparator plugin (Policy Tool only)
      verticalSeparator: { enabled: true },
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: false,
          padding: 15,
          boxWidth: 28,
          boxHeight: 12,
          font: {
            size: 11,
          },
          generateLabels: function(chart: any) {
            const labels = legendLabelsReflectLineDash.call(this, chart);

            labels.forEach((label: any) => {
              const idx = label.datasetIndex;
              const dataset = idx !== undefined ? chart.data.datasets[idx] : undefined;
              if (
                dataset?.label &&
                (String(dataset.label) === 'Target' || String(dataset.label).includes('Target:'))
              ) {
                label.strokeStyle = 'rgba(0, 0, 0, 1)';
                label.lineWidth = 2.5;
                label.fillStyle = 'rgba(0, 0, 0, 0)';
                label.lineDash = [12, 6];
              }
              if (label.text && label.text.length > 30) {
                label.text = label.text.substring(0, 27) + '...';
              }
            });

            return labels;
          }
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: true,
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            // Add LDV intensity (kg CO2e/mile) in tooltip so users can see linear intensity
            // even when emissions change due to VMT projections.
            const yearLabel = context?.label;
            const year = typeof yearLabel === 'string' ? parseInt(yearLabel, 10) : Number(yearLabel);
            if (Number.isFinite(year) && (label === 'LDV (Baseline)' || label === 'LDV') && vmtByYear) {
              const vmtRow = vmtByYear.get(year);
              const vmt = label === 'LDV (Baseline)' ? vmtRow?.baselineVmt : vmtRow?.policyVmt;
              if (typeof vmt === 'number' && Number.isFinite(vmt) && vmt > 0) {
                const tonsPerMile = value / vmt;
                const kgPerMile = tonsPerMile * 1000;
                return `${label}: ${value.toLocaleString()} MT CO₂e (${kgPerMile.toFixed(4)} kg CO₂e/mile)`;
              }
            }
            return `${label}: ${value.toLocaleString()} MT CO₂e`;
          },
        },
      },
      // Vertical separator is handled by the global plugin (opt-in via plugins.verticalSeparator.enabled)
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Year',
        },
        // Use offset to ensure first and last data points are fully visible
        offset: true,
        min: undefined, // Let Chart.js determine from data
        max: undefined, // Let Chart.js determine from data
        grid: {
          display: true,
          color: (context: any) => {
            // Highlight 2023 with a darker grid line
            const label = context.tick?.label;
            if (label === '2023') {
              return 'rgba(107, 114, 128, 0.6)';
            }
            return 'rgba(0, 0, 0, 0.06)';
          },
          lineWidth: (context: any) => {
            const label = context.tick?.label;
            return label === '2023' ? 2 : 1;
          },
        },
        ticks: {
          display: true,
          autoSkip: false,
          maxTicksLimit: 50, // Increased to show more years
          // Show labels every 5 years (2005, 2010, ...), and always show first/last
          // Exclude 2023 and 2024 from display
          callback: function(value: any, index: number, ticks: any[]) {
            const labels = (chartData as any)?.labels || [];
            if (labels.length === 0) return '';
            const label = labels[index];
            const yearNum = parseInt(String(label), 10);
            if (Number.isNaN(yearNum)) return '';
            
            // Exclude 2023 and 2024
            if (yearNum === 2023 || yearNum === 2024) {
              return '';
            }
            
            // Always show first and last year
            if (index === 0 || index === labels.length - 1) {
              return String(yearNum);
            }
            
            // Show every 5 years
            if (yearNum % 5 === 0) {
              return String(yearNum);
            }
            return '';
          },
          maxRotation: 0,
          minRotation: 0,
          font: {
            size: 11,
          },
        },
      },
      y: {
        beginAtZero: true,
        display: true,
        position: 'left' as const,
        // Set min/max based on actual data, excluding vertical separator
        min: lineChartYAxis.min,
        max: lineChartYAxis.max,
        title: {
          display: true,
          text: perCapitaMode ? 'metric tons CO₂e per person' : 'metric tons CO₂e',
          font: {
            size: 12,
            weight: 'normal' as const,
            color: '#374151',
          },
          padding: {
            top: 0,
            bottom: 10,
            left: 0,
            right: 0,
          },
          color: '#374151',
        },
        ticks: {
          display: true,
          showLabelBackdrop: false,
          stepSize: lineChartYAxis.stepSize,
          callback: function(value: any) {
            const numValue = typeof value === 'number' ? value : Number(value);
            if (Number.isNaN(numValue)) return '';
            return Math.round(numValue).toLocaleString();
          },
          font: {
            size: 11,
          },
          padding: 8,
          autoSkip: false,
          maxTicksLimit: 10,
          color: '#374151',
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)',
          drawBorder: true,
          drawOnChartArea: true,
        },
      },
    },
  };

  // Calculate total reduction for percentages
  const totalReduction = results.reductions.totalReduction;

  // Check if we have VMT component breakdown (matches tool's InterventionAreasControl)
  const hasVmtBreakdown = results.contributionsByPolicy.vmtReductionByComponent;
  const vmtComponents = results.contributionsByPolicy.vmtReductionByComponent;
  const cp = results.contributionsByPolicy;
  const sc = scenario as any;

  // Single source of truth: all and only policies in the tool, in display order.
  // When hasVmtBreakdown we show VMT as 3 components; otherwise as single VMT Reduction.
  const contributions = hasVmtBreakdown && vmtComponents
    ? [
        { name: 'Vehicle Electrification', value: Math.round(cp.evAdoption), setting: `${scenario.evAdoptionPercent}%` },
        { name: 'Energy Efficiency', value: Math.round(cp.energyEfficiency ?? 0), setting: `${sc.energyEfficiencyTargetPercent ?? sc.energyEfficiencyPercent ?? 0}%` },
        { name: 'VMT: Compact Development', value: Math.round(vmtComponents.transitOrientedDevelopment), setting: `${scenario.vmtReductionComponents.transitOrientedDevelopment.toFixed(1)}%` },
        { name: 'VMT: Active Transportation', value: Math.round(vmtComponents.activeTransportation), setting: `${scenario.vmtReductionComponents.activeTransportation.toFixed(1)}%` },
        { name: 'VMT: Transit Improvements', value: Math.round(vmtComponents.transitImprovements), setting: `${scenario.vmtReductionComponents.transitImprovements.toFixed(1)}%` },
        { name: 'Homes in/near downtown', value: Math.round(cp.downtownHomes ?? 0), setting: `${sc.downtownHomesPercent ?? 70}%` },
        { name: 'Parking Pricing', value: Math.round(cp.parkingPricing), setting: `${scenario.parkingPricing.priceIncreasePercent}% price increase` },
        { name: 'Expand Micromobility', value: Math.round(cp.micromobility ?? 0), setting: `${scenario.micromobility?.scalePercent ?? 0}% scale` },
        { name: 'Heat Pumps', value: Math.round(cp.heatPumpAdoption), setting: `${scenario.heatPumpAdoptionPercent}%` },
        { name: 'Solar Capacity', value: Math.round(cp.solarCapacity), setting: `${scenario.solarCapacityMultiplier}x` },
        { name: 'HDV Electrification', value: Math.round(cp.hdvElectrification), setting: `${scenario.hdvElectrificationPercent}%` },
        { name: 'Organics Diversion', value: Math.round(cp.organicsDiversion ?? 0), setting: `${sc.organicsDiversionPercent ?? 50}%` },
      ]
    : [
        { name: 'Vehicle Electrification', value: Math.round(cp.evAdoption), setting: `${scenario.evAdoptionPercent}%` },
        { name: 'Energy Efficiency', value: Math.round(cp.energyEfficiency ?? 0), setting: `${sc.energyEfficiencyTargetPercent ?? sc.energyEfficiencyPercent ?? 0}%` },
        { name: 'VMT Reduction', value: Math.round(cp.vmtReduction), setting: `${scenario.vmtReductionPercent}%` },
        { name: 'Homes in/near downtown', value: Math.round(cp.downtownHomes ?? 0), setting: `${sc.downtownHomesPercent ?? 70}%` },
        { name: 'Parking Pricing', value: Math.round(cp.parkingPricing), setting: `${scenario.parkingPricing.priceIncreasePercent}% price increase` },
        { name: 'Expand Micromobility', value: Math.round(cp.micromobility ?? 0), setting: `${scenario.micromobility?.scalePercent ?? 0}% scale` },
        { name: 'Heat Pumps', value: Math.round(cp.heatPumpAdoption), setting: `${scenario.heatPumpAdoptionPercent}%` },
        { name: 'Solar Capacity', value: Math.round(cp.solarCapacity), setting: `${scenario.solarCapacityMultiplier}x` },
        { name: 'HDV Electrification', value: Math.round(cp.hdvElectrification), setting: `${scenario.hdvElectrificationPercent}%` },
        { name: 'Organics Diversion', value: Math.round(cp.organicsDiversion ?? 0), setting: `${sc.organicsDiversionPercent ?? 50}%` },
      ];

  const fmtTons = (v: number) => (v >= 1000 ? `${Math.round(v / 100) / 10}k` : `${Math.round(v)}`);

  // Use full policy name as bar label (no truncation); setting and value go in tooltip
  const contributionsData = {
    labels: contributions.map((c) => c.name),
    datasets: [
      {
        label: 'GHG Reduction (MT CO₂e)',
        data: contributions.map((c) => c.value),
        backgroundColor: hasVmtBreakdown
          ? [
              'rgba(59, 130, 246, 0.8)', // EV Adoption - Blue
              'rgba(5, 150, 105, 0.8)', // Energy Efficiency - Green-600
              'rgba(5, 150, 105, 0.8)', // VMT: TOD - Green-600
              'rgba(16, 185, 129, 0.8)', // VMT: Active Transport - Green-500
              'rgba(110, 231, 183, 0.8)', // VMT: Transit - Green-300
              'rgba(52, 211, 153, 0.8)', // Homes in/near downtown - Green-400
              'rgba(34, 197, 94, 0.8)', // Parking Pricing - Green-600
              'rgba(22, 163, 74, 0.8)', // Micromobility - Green-700
              'rgba(245, 158, 11, 0.8)', // Heat Pumps - Orange
              'rgba(139, 92, 246, 0.8)', // Solar - Purple
              'rgba(236, 72, 153, 0.8)', // HDV - Pink
              'rgba(20, 184, 166, 0.8)', // Organics - Teal
            ]
          : [
              'rgba(59, 130, 246, 0.8)',
              'rgba(5, 150, 105, 0.8)',
              'rgba(16, 185, 129, 0.8)',
              'rgba(52, 211, 153, 0.8)', // Homes in/near downtown
              'rgba(34, 197, 94, 0.8)', // Parking Pricing - Green-600
              'rgba(22, 163, 74, 0.8)', // Micromobility - Green-700
              'rgba(245, 158, 11, 0.8)',
              'rgba(139, 92, 246, 0.8)',
              'rgba(236, 72, 153, 0.8)',
            ],
        borderColor: hasVmtBreakdown
          ? [
              'rgba(59, 130, 246, 1)',
              'rgba(5, 150, 105, 1)',
              'rgba(5, 150, 105, 1)',
              'rgba(16, 185, 129, 1)',
              'rgba(110, 231, 183, 1)',
              'rgba(52, 211, 153, 1)', // Homes in/near downtown
              'rgba(34, 197, 94, 1)',
              'rgba(22, 163, 74, 1)',
              'rgba(245, 158, 11, 1)',
              'rgba(139, 92, 246, 1)',
              'rgba(236, 72, 153, 1)',
              'rgba(20, 184, 166, 1)',
            ]
          : [
              'rgba(59, 130, 246, 1)',
              'rgba(5, 150, 105, 1)',
              'rgba(16, 185, 129, 1)',
              'rgba(52, 211, 153, 1)',
              'rgba(34, 197, 94, 1)',
              'rgba(22, 163, 74, 1)',
              'rgba(245, 158, 11, 1)',
              'rgba(139, 92, 246, 1)',
              'rgba(236, 72, 153, 1)',
            ],
        borderWidth: 2,
      },
    ],
  };

  const contributionsOptions = {
    type: 'bar' as const,
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y' as const,
    layout: {
      padding: {
        top: 10, // Reduced to minimize white space
        bottom: 12, // Reduced to minimize white space
        left: 24,
        right: 24,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (context: any) => {
            const idx = context.dataIndex;
            const c = contributions[idx];
            const value = context.parsed.x;
            const percentage = totalReduction > 0 ? ((value / totalReduction) * 100).toFixed(1) : '0';
            return [
              `${c.name}: ${c.setting}, ${fmtTons(value)} t`,
              `Reduction: ${value.toLocaleString()} MT CO₂e`,
              `Contribution: ${percentage}% of total`,
            ];
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'GHG Reduction (Metric Tons CO₂e)',
        },
        ticks: {
          callback: (value: any) => Number(value).toLocaleString(),
        },
      },
      y: {
        ticks: {
          autoSkip: false,
          padding: 6,
        },
        grid: {
          display: false,
        },
      },
    },
  };

  return chartType === 'line' ? (
    /* Line Chart */
    <div className="chart-section">
      <ChartRendererSupabase
        chartData={chartData}
        chartOptions={chartOptions}
        height="400px"
        width="100%"
      />
    </div>
  ) : chartType === 'contributions' ? (
    /* Contributions Chart */
    <div className="chart-section">
      <ChartRendererSupabase
        chartData={contributionsData}
        chartOptions={contributionsOptions}
        height="400px"
      />
    </div>
  ) : chartType === 'stacked' ? (
    /* Stacked Area Chart */
    <div className="chart-section">
      <ChartRendererSupabase
        chartData={stackedChartData || { labels: [], datasets: [] }}
        chartOptions={stackedChartOptions}
        height="400px"
        width="100%"
      />
    </div>
  ) : (
    /* Forecast Chart */
    <div className="chart-section">
      <ChartRendererSupabase
        chartData={forecastChartData || { labels: [], datasets: [] }}
        chartOptions={forecastChartOptions}
        height="400px"
      />
    </div>
  );
};

export default PolicyImpactChart;
